import {
  parseAsArrayOf,
  parseAsBoolean,
  parseAsInteger,
  parseAsIsoDate,
  parseAsString,
  parseAsStringLiteral,
  useQueryStates,
} from 'nuqs';
import type { PoolFilters, PoolSortField } from '../types/Checks';
import type { SortDirection } from '../types/Filters';
import { toApiDate } from '../utils/data';
import {
  allFiltersCleared,
  hasAnyFilter,
  idsOrNull,
  SORT_DIRECTIONS,
  selectionsFrom,
  textOrNull,
  toPercentageBound,
} from '../utils/filters';
import type { PercentageRange, SelectedOption } from './useAllocationFilters';

const SORT_FIELDS = [
  'professional',
  'profile',
  'project',
  'allocatedPercentage',
  'startDate',
  'noticeDate',
  'noticeDays',
  'endDate',
] as const satisfies readonly PoolSortField[];

const PARSERS = {
  date: parseAsIsoDate,
  professionalIds: parseAsArrayOf(parseAsInteger),
  professionalNames: parseAsArrayOf(parseAsString),
  professionalSearch: parseAsString,
  profileSearch: parseAsString,
  projectIds: parseAsArrayOf(parseAsInteger),
  minPercentage: parseAsInteger,
  maxPercentage: parseAsInteger,
  billable: parseAsBoolean,
  notified: parseAsBoolean,
  notes: parseAsString,
  tagIds: parseAsArrayOf(parseAsInteger),
  sortBy: parseAsStringLiteral(SORT_FIELDS),
  sortDirection: parseAsStringLiteral(SORT_DIRECTIONS),
  page: parseAsInteger.withDefault(1),
  perPage: parseAsInteger.withDefault(20),
};

export type PoolSearchKey = 'professionalSearch' | 'profileSearch' | 'notes';
export type PoolIdsKey = 'projectIds' | 'tagIds';
/** Um filtro removível pelo chip — a faixa de % sai inteira, não meia faixa. */
export type PoolFilterKey =
  | 'professional'
  | 'percentage'
  | 'billable'
  | 'notified'
  | PoolSearchKey
  | PoolIdsKey;

/** Rascunho do painel "Filtros avançados", comitado de uma vez no "Aplicar". */
export type PoolAdvancedValues = {
  professionals: SelectedOption[];
  profileSearch: string;
  projectIds: number[];
  billable: boolean | null;
  notified: boolean | null;
  notes: string;
  tagIds: number[];
};

const CLEARED_BY_KEY = {
  professional: { professionalIds: null, professionalNames: null },
  professionalSearch: { professionalSearch: null },
  profileSearch: { profileSearch: null },
  projectIds: { projectIds: null },
  percentage: { minPercentage: null, maxPercentage: null },
  billable: { billable: null },
  notified: { notified: null },
  notes: { notes: null },
  tagIds: { tagIds: null },
} as const satisfies Record<PoolFilterKey, object>;

const ALL_FILTERS_CLEARED = allFiltersCleared(CLEARED_BY_KEY);

/** Estado do Pool na URL, no molde de `useAllocationFilters`; filtro ou data novos voltam à página 1. */
export function usePoolFilters() {
  const [state, setState] = useQueryStates(PARSERS, { throttleMs: 300 });

  const filters: PoolFilters = {
    date: toApiDate(state.date) ?? undefined,
    professionalIds: state.professionalIds ?? undefined,
    professionalSearch: state.professionalSearch ?? undefined,
    profileSearch: state.profileSearch ?? undefined,
    projectIds: state.projectIds ?? undefined,
    minPercentage: state.minPercentage ?? undefined,
    maxPercentage: state.maxPercentage ?? undefined,
    billable: state.billable ?? undefined,
    notified: state.notified ?? undefined,
    notes: state.notes ?? undefined,
    tagIds: state.tagIds ?? undefined,
    sortBy: state.sortBy ?? undefined,
    sortDirection: state.sortBy ? (state.sortDirection ?? 'asc') : undefined,
    page: state.page,
    perPage: state.perPage !== 20 ? state.perPage : undefined,
  };

  const professionals = selectionsFrom(state.professionalIds, state.professionalNames);

  const hasActiveFilters = hasAnyFilter(CLEARED_BY_KEY, state);

  return {
    filters,
    date: state.date,
    professionals,
    hasActiveFilters,
    setDate: (date: Date | null) => setState({ date, page: 1 }),
    setSearch: (key: PoolSearchKey, value: string) =>
      setState({ [key]: textOrNull(value), page: 1 }),
    setIds: (key: PoolIdsKey, ids: number[]) => setState({ [key]: idsOrNull(ids), page: 1 }),
    setPercentageRange: (range: PercentageRange) =>
      setState({
        minPercentage: toPercentageBound(range.min),
        maxPercentage: toPercentageBound(range.max),
        page: 1,
      }),
    setBillable: (billable: boolean | null) => setState({ billable, page: 1 }),
    setNotified: (notified: boolean | null) => setState({ notified, page: 1 }),
    /** `'none'` volta à ordem do legado. */
    setSort: (field: PoolSortField, direction: SortDirection | 'none') =>
      setState(
        direction === 'none'
          ? { sortBy: null, sortDirection: null }
          : { sortBy: field, sortDirection: direction },
      ),
    applyAdvanced: (values: PoolAdvancedValues) =>
      setState({
        professionalIds: idsOrNull(values.professionals.map((option) => option.id)),
        professionalNames:
          values.professionals.length > 0
            ? values.professionals.map((option) => option.name)
            : null,
        profileSearch: textOrNull(values.profileSearch),
        projectIds: idsOrNull(values.projectIds),
        billable: values.billable,
        notified: values.notified,
        notes: textOrNull(values.notes),
        tagIds: idsOrNull(values.tagIds),
        page: 1,
      }),
    clearFilter: (key: PoolFilterKey) => setState({ ...CLEARED_BY_KEY[key], page: 1 }),
    /** Limpa os filtros, não a ordenação nem a data. */
    clearFilters: () => setState({ ...ALL_FILTERS_CLEARED, page: 1 }),
    perPage: state.perPage,
    setPerPage: (perPage: number) => setState({ perPage, page: 1 }),
    goToPage: (page: number) => setState({ page }),
  };
}

export type PoolFilterState = ReturnType<typeof usePoolFilters>;
